import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "./Header";
import { MasajidContext } from "../contexts/MasajidContext";
import Fuse from "fuse.js";
import BottomNavBar from "./BottomNavBar";
import { Heart, UserStar } from "lucide-react";

function SearchResults() {
  const { masajid, language, toggleFavourite, isFavourite, isMyMasjid } =
    useContext(MasajidContext);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const handleSearch = useCallback(
    (searchTerm) => {
      if (!searchTerm.trim()) {
        return;
      }

      const allMasjids = masajid;
      const fuse = new Fuse(allMasjids, {
        keys: ["engName", "urdName"],
        threshold: 0.3,
      });

      const result = fuse.search(searchTerm);
      const matched = result
        .map((r) => r.item)
        .sort((a, b) => a.engName.localeCompare(b.engName));

      if (matched.length === 0) {
        setNoResults(true);
        setResults([]);
      } else {
        setResults(matched);
        setNoResults(false);
      }
      setSuggestions([]);
    },
    [masajid],
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get("q");

    if (query && masajid.length > 0) {
      setSearch(query);
      handleSearch(query);
    }
  }, [location.search, masajid, handleSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setSuggestions([]);
      return;
    }

    const fuse = new Fuse(masajid, {
      keys: ["engName", "urdName"],
      threshold: 0.3,
    });

    const results = fuse.search(search);
    const matched = results
      .map((r) => r.item)
      .sort((a, b) => a.engName.localeCompare(b.engName));

    setSuggestions(matched.slice(0, 10));
  }, [search, masajid]);

  const handleSubmit = () => {
    if (search.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(search)}`);
    }
  };

  const handleViewDetails = (masjid) => {
    navigate(`/masjid/${masjid.username}`);
  };

  const handleToggleFavourite = (e, username) => {
    e.stopPropagation(); // Prevent triggering the card click
    toggleFavourite(username);
  };

  const handleSuggestionClick = (masjid) => {
    setSearch("");
    setSuggestions([]);
    setIsFocused(false);
    navigate(`/masjid/${masjid.username}`);
  };

  const handleBlur = () => {
    setTimeout(() => setIsFocused(false), 200);
  };

  return (
    <>
      <Header />

      <section className="section">
        {!isOnline && (
          <div style={{ marginTop: 64, textAlign: "center" }}>
            <h1
              style={{ color: "#f63c3c", marginTop: 64 }}
              lang={language === "urdu" && "ur"}
            >
              {language === "urdu"
                ? "آپ کا انٹرنیٹ کام نہیں کر رہا"
                : "Please connect to the internet"}
            </h1>
          </div>
        )}

        {isOnline && (
          <div className="d-flex d-md-flex justify-content-center justify-content-md-center">
            <div
              className="input-group"
              style={{
                width: "95%",
                maxWidth: "800px",
                background: "#041b23",
                borderRadius: "40px",
                border: "3px solid rgb(27, 179, 200)",
                display: "flex",
                padding: "8px",
                marginBottom: "8px",
                marginTop: "24px",
              }}
            >
              <input
                className="py-2 mr-1 pr-5 search-bar"
                type="search"
                ref={inputRef}
                placeholder="Masjid Name (Eng/Urdu)"
                name="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={handleBlur}
              />
              <span
                className="input-group-text input-group-append"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                }}
              >
                <button
                  className="search-button"
                  type="button"
                  onClick={handleSubmit}
                >
                  <i
                    className="fas fa-search"
                    style={{ color: "rgb(255, 255, 255, 0.9)" }}
                  />
                </button>
              </span>
              {isFocused && suggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "#041b23",
                    border: "3px solid #041b23",
                    borderRadius: 12,
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 1000,
                    marginTop: 4,
                  }}
                >
                  {suggestions.map((masjid) => (
                    <div
                      key={masjid.username}
                      onClick={() => handleSuggestionClick(masjid)}
                      className="suggestion"
                    >
                      <p
                        style={{
                          margin: 0,
                          textAlign: language === "urdu" ? "right" : "left",
                          fontSize: "1.1rem",
                          color: "rgb(255, 255, 255, 0.8)",
                        }}
                        lang={language === "urdu" && "ur"}
                        dir={language === "urdu" ? "rtl" : "ltr"}
                      >
                        {language === "urdu" ? masjid.urdName : masjid.engName}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "0.9rem",
                          color: "rgb(255, 255, 255, 0.5)",
                          textAlign: language === "urdu" ? "right" : "left",
                        }}
                        lang={language === "urdu" && "ur"}
                        dir={language === "urdu" ? "rtl" : "ltr"}
                      >
                        {language === "urdu" ? masjid.addressUrdu : masjid.address}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {noResults && (
          <div className="d-flex justify-content-center">
            <div
              className="card fade-in"
              style={{
                width: "95%",
                maxWidth: "800px",
                background: "radial-gradient(circle, #0a3545, #041b23)",
                border: "3px solid rgb(27, 179, 200, 0.3)",
                borderRadius: 24,
                color: "#ffffff",
                marginTop: 24,
                boxShadow: "5px 5px 15px #021015",
                padding: 5,
              }}
            >
              <div className="card-body">
                <h4
                  className="card-title"
                  style={{ textAlign: "center", marginBottom: 0 }}
                  lang={language === "urdu" && "ur"}
                >
                  {language === "urdu" ? "کوئی نتیجہ نہیں ملا" : "No results found"}
                </h4>
              </div>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            <p
              style={{
                textAlign: "center",
                color: "rgb(255, 255, 255, 0.6)",
                fontSize: "0.9rem",
                marginBottom: 18,
              }}
              lang={language === "urdu" && "ur"}
              dir={language === "urdu" ? "rtl" : "ltr"}
            >
              {language === "urdu"
                ? `${results.length} ${results.length === 1 ? "مسجد" : "مساجد"}`
                : `${results.length} ${results.length === 1 ? "Masjid" : "Masajid"} found`}
            </p>

            {results.map((masjid, index) => (
              <div
                key={masjid.username}
                className="d-flex justify-content-center fade-in"
                onClick={() => handleViewDetails(masjid)}
                style={{
                  animationDelay: `${index * 50}ms`,
                  background: "radial-gradient(circle, #0a3545, #041b23)",
                  borderRadius: "12px",
                  padding: "12px",
                  marginTop: "10px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: "3px solid rgb(27, 179, 200, 0.3)",
                  width: "95%",
                  maxWidth: "800px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: language === "urdu" ? "row-reverse" : "row",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        textAlign: language === "urdu" ? "right" : "left",
                        minWidth: 0,
                      }}
                    >
                      <h6
                        style={{
                          margin: "0 0 4px 0",
                          color: "#ffffff",
                          fontSize: "1rem",
                          textAlign: language === "urdu" ? "right" : "left",
                        }}
                        lang={language === "urdu" && "ur"}
                      >
                        {language === "urdu" ? masjid.urdName : masjid.engName}
                      </h6>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginLeft: language === "urdu" ? "0" : "10px",
                        marginRight: language === "urdu" ? "10px" : "0",
                      }}
                    >
                      {isMyMasjid(masjid.username) && (
                        <UserStar
                          size={24}
                          color="#22c55e"
                          fill="#22c55e"
                          strokeWidth={2}
                        />
                      )}
                      <div
                        onClick={(e) =>
                          handleToggleFavourite(e, masjid.username)
                        }
                        className="heart-icon-container"
                        style={{
                          flexShrink: 0,
                          cursor: "pointer",
                          padding: "4px",
                          borderRadius: "50%",
                        }}
                      >
                        <Heart
                          size={24}
                          color={
                            isFavourite(masjid.username)
                              ? "#e63946"
                              : "rgb(255, 255, 255, 0.6)"
                          }
                          fill={
                            isFavourite(masjid.username) ? "#e63946" : "none"
                          }
                          strokeWidth={2}
                        />
                      </div>
                    </div>
                  </div>
                  <p
                    style={{
                      marginBottom: 12,
                      color: "rgb(255, 255, 255, 0.6)",
                      fontSize: "0.8rem",
                      textAlign: language === "urdu" ? "right" : "left",
                      maxWidth: "73%",
                      marginLeft: language === "urdu" ? "auto" : "0",
                    }}
                    lang={language === "urdu" && "ur"}
                    dir={language === "urdu" ? "rtl" : "ltr"}
                  >
                    {language === "urdu" ? masjid.addressUrdu : masjid.address}
                  </p>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      marginBottom: 0,
                      color: "rgb(7,240,255)",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                    lang={language === "urdu" && "ur"}
                  >
                    {language === "urdu"
                      ? "تفصیلات دیکھنے کے لیے یہاں دبائیں"
                      : "Click to view details"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <BottomNavBar />
    </>
  );
}

export default SearchResults;
